import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';

import { Loader } from '@/components/core/Loader';
import { getDataModel, getInjector, getRootScope } from '@/services/angularjs-bridge';
import { DataModel } from '@/types/DataModel';

interface DataModelContextInterface {
    dataModel?: DataModel;
}

const DataModelContext = createContext<DataModelContextInterface | undefined>(undefined);

const useDataModel = () => {

    const dataModelContext = useContext(DataModelContext);

    if (!dataModelContext) {
        throw new Error('useDataModel must be used within a DataModelProvider');
    }

    return dataModelContext;
};

const DataModelProvider = ({ children }: PropsWithChildren) => {

    const [dataModel, setDataModel] = useState<DataModel | undefined>(undefined);
    const [isDataModelLoading, setIsDataModelLoading] = useState(false);

    useEffect(() => {
        // Grab the data model from the angularjs world, and wait
        // for it in case it's still loading
        const angularJsInjector = getInjector();
        const $rootScope = getRootScope(angularJsInjector);

        if ($rootScope.isDataModelLoading || !$rootScope.isDataModelLoaded) {
            setIsDataModelLoading(true);
            $rootScope.$on('dataModelLoaded', () => {
                const dataModel = getDataModel();
                setDataModel(dataModel);
                setIsDataModelLoading(false);
            });
        } else {
            const dataModel = getDataModel();
            setDataModel(dataModel);
            setIsDataModelLoading(false);
        }
    }, []);

    if (!dataModel || isDataModelLoading) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center">
                <Loader />
            </div>
        );
    } else {
        return <DataModelContext.Provider value={{ dataModel }}>{children}</DataModelContext.Provider>;
    }

};

export { DataModelContext, DataModelProvider, useDataModel };
